import React, { useState } from 'react'
import Head from 'next/head'
import styled from 'styled-components'
import {
  CloseButton,
  Button,
  Flex,
  Link,
  Text,
  Box,
  getPaletteColor,
  Image
} from 'pcln-design-system'
import { Referral } from '@/types'
import useAppBannerForIosSafari from './useAppBannerForIosSafari'

const COOKIE_MAX_AGE = 2592000 // 30 Days

const Container = styled(Flex)`
  background-color: ${getPaletteColor('background.base')};
  color: ${getPaletteColor('text.lightest')};
  padding: 8px;
  @media (min-width: 400px) {
    padding-right: 24px;
  }
`

const StyledLink = styled(Link)`
  flex: 1 1 auto;
  display: flex;
  &:active,
  &:focus,
  &:visited,
  &:hover {
    color: ${getPaletteColor('text.lightest')};
    text-decoration: none;
  }
`

const appIconUrl =
  'https://s1.pclncdn.com/design-assets/brand-assets/app-icon.svg'

type AppBannerProps = {
  titleText: string
  contentText: string
  pageId: string
  referral: Referral
  clientGUID: string
}

export default function AppBanner({
  titleText,
  contentText,
  pageId,
  referral,
  clientGUID
}: AppBannerProps) {
  const [isBannerVisible, setBannerVisible] = useState(true)
  const [iOSSafari, impressionUrl] = useAppBannerForIosSafari(
    referral,
    clientGUID
  )

  const closeAppBanner = () => {
    setBannerVisible(false)
    document.cookie = `hideAppBanner=true; domain=.priceline.com; path=/; max-age=${COOKIE_MAX_AGE};`
  }

  const downloadAppLink = `https://www.priceline.com/r/app/?channel=Display&product=App&theme=app_dl_install\
    &PAGEID=${pageId}_v5\
    &sub_site_id=${clientGUID}\
    &refid=${referral?.id ?? ''}\
    &refclickid=${referral?.clickId ?? ''}
  `

  if (iOSSafari) {
    return (
      <>
        <Head>
          <meta
            name="apple-itunes-app"
            content="app-id=336381998"
            key="apple-smart-banner"
          />
        </Head>
        <Image src={impressionUrl} height="0" width="1" border="0" />
      </>
    )
  }

  return isBannerVisible ? (
    <Container
      id="app-download-banner"
      className="app-banner"
      fontSize={0}
      m={1}
      borderRadius="lg"
    >
      <Flex alignItems="center" mr={12}>
        <CloseButton
          style={{ color: '#0068ef' }}
          size={24}
          id="closeBannerBtn"
          data-ga-category="PCLN_HOME"
          data-ga-action="App_Banner"
          data-ga-label="dismiss"
          title="dismiss app banner"
          onClick={closeAppBanner}
        />
      </Flex>
      <StyledLink
        color="text.lightest"
        href={downloadAppLink}
        data-testid="DASH_MWEB_TAB_GET_APP"
        data-ga-category="PCLN_HOME"
        data-ga-action="App_Banner"
        data-ga-label="click to launch"
      >
        <Flex alignItems="flex-start" pr={12} py={0} width={52}>
          <Image src={appIconUrl} alt="Priceline Icon" />
        </Flex>
        <Flex flexDirection="column" justifyContent="center">
          <Text
            fontWeight="bold"
            textStyle="captionBold"
            color="background.darkest"
          >
            {titleText}
          </Text>
          <Text textStyle="disclaimer" color="background.dark">
            {contentText}
          </Text>
        </Flex>
        <Flex alignItems="center" ml="auto">
          <Box ml="auto" my="auto" pl={1}>
            <Button size="small">Get</Button>
          </Box>
        </Flex>
      </StyledLink>
    </Container>
  ) : null
}
